/**
 * Turn a query string into ONE hydration patch for the reducer.
 *
 * This replaces a 230-line useEffect that parsed params and then called ~13 individual setState
 * functions in a loop over a hand-written switch. Returning a plain object instead means the
 * whole restore is a single atomic dispatch, which is what lets HYDRATED reconcile visibility
 * correctly and removes the need for isLoadingFromUrlRef and its 500ms timer.
 *
 * Parsing is otherwise FAITHFUL to the previous behavior, including the name-based lookups.
 * Switching share URLs to IDs is Step 8; doing it here would conflate a state refactor with a
 * URL-format change, and links already sent to prospects must keep working either way.
 *
 * Pure: takes a search string, returns data. No window access, so it is testable directly.
 */

import {
  primaryProducts,
  addonSubscriptions,
  oneTimeProducts,
  questions,
} from '../../lib/pricing/index.js';
import { decodeConfig, validateConfig, applyConfigToState } from '../../utils/config-encoder';
import { CUSTOMER_FIELD_NAMES } from './checkoutSchema';

/** Params that carry a shared configuration. */
const SELECTION_PARAMS = ['plan', 'term', 'addons', 'services', 'coupon', 'answers'];

/**
 * Share URLs historically encoded display NAMES, and the encoder replaced spaces with '+'. A
 * raw URLSearchParams read already converts '+' to a space, but these values also arrive
 * percent-encoded from some mail clients, so decode then normalize remaining '+' characters.
 */
function decodeName(raw) {
  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch {
    // A malformed escape sequence should not take down the page; use the raw value.
  }
  return value.replace(/\+/g, ' ');
}

/**
 * Resolve one token to a catalog item, accepting EITHER an id or a display name.
 *
 * WRITE IDS, READ BOTH. buildShareParams now writes ids, because the previous scheme keyed on
 * plan.name -- so renaming "Business Plan" in the config silently broke every URL an account
 * manager had emailed a prospect. It failed quietly: a console.warn, the default plan shown,
 * and nobody the wiser.
 *
 * The name branch is kept INDEFINITELY, not as a deprecation window. Links live in email
 * threads forever, so a URL sent today must still resolve years from now.
 *
 * Id is tried first: if a product's name ever collided with another product's id, matching by
 * name first would resolve to the wrong item.
 */
function resolveItem(token, catalogItems) {
  return (
    catalogItems.find((candidate) => candidate.id === token) ||
    catalogItems.find((candidate) => candidate.name === token) ||
    null
  );
}

/** Map a comma-separated list of ids-or-names onto an { id: true } map. */
function tokensToIdMap(raw, catalogItems, unresolved) {
  const map = {};

  decodeName(raw)
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const item = resolveItem(token, catalogItems);
      if (item) map[item.id] = true;
      else unresolved.push(token);
    });

  return map;
}

/**
 * Parse the `answers` param: questionId:answerId pairs, comma separated, with '+' joining
 * multiple answers to one checkbox question.
 *
 *   answers=logo-question:logo-yes,interests:seo+ads
 *
 * questionAnswers was previously DROPPED from share URLs entirely, so an account manager who
 * configured $2,000 of upsells and sent the link showed the prospect a quote without them --
 * the share feature misrepresented the very thing it existed to communicate.
 *
 * Every id is validated against the catalog. An unresolvable pair is reported rather than
 * written into state, so a stale link cannot smuggle in an answer id that no longer prices.
 */
function parseAnswers(raw, questionList, unresolved) {
  const answers = {};

  decodeName(raw)
    .split(',')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const [questionId, answerPart] = pair.split(':');
      if (!questionId || !answerPart) {
        unresolved.push(pair);
        return;
      }

      const question = questionList.find((q) => q.id === questionId);
      if (!question) {
        unresolved.push(questionId);
        return;
      }

      const answerIds = answerPart
        .split('+')
        .map((id) => id.trim())
        .filter(Boolean)
        .filter((id) => {
          const exists = question.answers.some((a) => a.id === id);
          if (!exists) unresolved.push(id);
          return exists;
        });

      if (answerIds.length === 0) return;

      // Shape must match what the reducer and resolveCart expect: a bare string for radio,
      // an { answerId: true } map for checkbox.
      if (question.type === 'checkbox') {
        answers[questionId] = answerIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
      } else {
        answers[questionId] = answerIds[0];
      }
    });

  return answers;
}

/**
 * Read every supported param shape and return a hydration patch, or null when there is
 * nothing to restore.
 *
 * @param {string} search location.search
 * @param {object} [deps] injection point for tests (localStorage reader)
 * @returns {?{selection?, customer?, coupon?, showResumeMessage?, unresolvedParams?, shouldCleanUrl?}}
 */
export function readHydration(search, { loadSavedState = readSavedState } = {}) {
  const params = new URLSearchParams(search || '');
  const unresolvedParams = [];

  const hasSelectionParams = SELECTION_PARAMS.some((key) => params.get(key));
  const hasCustomerParams = CUSTOMER_FIELD_NAMES.some((key) => params.get(key));
  const configParam = params.get('config');

  // ---- Human-readable params (the preferred, current format) --------------------------
  if (hasSelectionParams || hasCustomerParams) {
    const selection = {};
    const customer = {};
    const coupon = {};

    const planParam = params.get('plan');
    if (planParam) {
      const token = decodeName(planParam);
      const plan = resolveItem(token, primaryProducts);
      if (plan) selection.primaryPlanId = plan.id;
      else unresolvedParams.push(token);
    }

    const termParam = params.get('term');
    if (termParam && ['monthly', 'yearly'].includes(termParam)) {
      selection.term = termParam;
    } else if (termParam) {
      unresolvedParams.push(termParam);
    }

    const addonsParam = params.get('addons');
    if (addonsParam) {
      selection.addonIds = tokensToIdMap(addonsParam, addonSubscriptions, unresolvedParams);
    }

    const servicesParam = params.get('services');
    if (servicesParam) {
      selection.oneTimeIds = tokensToIdMap(servicesParam, oneTimeProducts, unresolvedParams);
    }

    const answersParam = params.get('answers');
    if (answersParam) {
      selection.questionAnswers = parseAnswers(answersParam, questions, unresolvedParams);
    }

    const couponParam = params.get('coupon');
    if (couponParam) {
      coupon.code = couponParam;
      coupon.expanded = true;
    }

    CUSTOMER_FIELD_NAMES.forEach((name) => {
      const value = params.get(name);
      if (value) customer[name] = value;
    });

    return {
      selection,
      customer,
      coupon,
      unresolvedParams,
      shouldCleanUrl: true,
    };
  }

  // ---- Legacy Base64 ?config= --------------------------------------------------------
  // Kept because links in old email threads still carry it. The decoder is the only consumer
  // of src/utils/config-encoder.js's decode path.
  if (configParam) {
    const decoded = decodeConfig(configParam);
    if (!decoded) {
      return { unresolvedParams: ['config'], shouldCleanUrl: true };
    }

    const { config, warnings } = validateConfig(decoded, {
      primaryProducts,
      addonSubscriptions,
      oneTimeProducts,
    });
    const legacy = applyConfigToState(config);

    // applyConfigToState still speaks the old selected* vocabulary; translate once, here, so
    // nothing downstream has to know about it.
    const selection = {};
    if (legacy.selectedPrimaryPlan) selection.primaryPlanId = legacy.selectedPrimaryPlan;
    if (legacy.selectedTerm) selection.term = legacy.selectedTerm;
    if (legacy.selectedAddonSubscriptions) selection.addonIds = legacy.selectedAddonSubscriptions;
    if (legacy.selectedOneTimeProducts) selection.oneTimeIds = legacy.selectedOneTimeProducts;
    if (legacy.questionAnswers) selection.questionAnswers = legacy.questionAnswers;

    return {
      selection,
      coupon: legacy.couponCode
        ? { code: legacy.couponCode, expanded: Boolean(legacy.showCouponField) }
        : undefined,
      unresolvedParams: [...unresolvedParams, ...(warnings || [])],
      shouldCleanUrl: true,
    };
  }

  // ---- Returning from a cancelled Stripe checkout -------------------------------------
  // Only when no config params are present: an explicit shared link must win over a saved cart.
  if (params.get('canceled') === 'true') {
    const saved = loadSavedState();
    if (saved) return { ...saved, showResumeMessage: true, shouldCleanUrl: true };
  }

  return null;
}

/**
 * Build a shareable URL's params from current state. The inverse of readHydration.
 *
 * Writes IDS, not display names. The previous builder keyed on plan.name, and the reader
 * matched that string exactly -- so renaming "Business Plan" in the config broke every link
 * already sitting in a prospect's inbox, quietly: console.warn, default plan, no signal to
 * anyone. product-config.mjs already warns that ids must stay stable, which suggests ids were
 * always the intended key and this drifted.
 *
 * Includes `answers`, which the old builder omitted entirely.
 *
 * @param {object} state {selection, customer, coupon}
 * @param {object} [opts] includeCustomer -- PII is opt-in, see the note in the modal
 */
export function buildShareParams({ selection, customer, coupon }, { includeCustomer = true } = {}) {
  const params = new URLSearchParams();

  if (selection?.primaryPlanId) params.set('plan', selection.primaryPlanId);
  if (selection?.term) params.set('term', selection.term);

  const selectedIds = (map) => Object.keys(map || {}).filter((id) => map[id]);

  const addons = selectedIds(selection?.addonIds);
  if (addons.length) params.set('addons', addons.join(','));

  const services = selectedIds(selection?.oneTimeIds);
  if (services.length) params.set('services', services.join(','));

  // questionId:answerId pairs; '+' joins multiple answers to one checkbox question.
  const answers = Object.entries(selection?.questionAnswers || {})
    .map(([questionId, value]) => {
      const ids =
        typeof value === 'string' ? [value] : selectedIds(value);
      return ids.length ? `${questionId}:${ids.join('+')}` : null;
    })
    .filter(Boolean);
  if (answers.length) params.set('answers', answers.join(','));

  if (coupon?.code) params.set('coupon', coupon.code);

  if (includeCustomer) {
    CUSTOMER_FIELD_NAMES.forEach((name) => {
      const value = customer?.[name];
      if (value) params.set(name, value);
    });
  }

  return params;
}

/**
 * How long a saved checkout stays eligible for restore.
 *
 * Dropped from 24 hours to 2. This window is also how long the saved blob lingers, and the
 * flow it exists for -- leave for Stripe, abandon, come back -- is a matter of minutes. A
 * 24-hour window mostly meant a day of stale carts on shared and kiosk browsers.
 */
export const SAVED_STATE_MAX_AGE_MS = 2 * 60 * 60 * 1000; // 2 hours

const STORAGE_KEY = 'checkoutFormState';

/**
 * PII AND PERSISTENCE.
 *
 * Customer fields are written ONLY when the user actually leaves for Stripe -- see
 * persistForCheckout below -- while the cart itself is written continuously.
 *
 * The fix here is scope reduction, not encryption: any key shipped in client JS is readable by
 * the same XSS that would read the plaintext, so encrypting would be theatre. What actually
 * reduces exposure is not writing PII in the first place unless it is needed. Previously every
 * keystroke wrote name, email, and phone to localStorage, so a visitor who typed their details
 * and abandoned WITHOUT submitting still left them on the device for a day.
 *
 * The cancel-resume flow is preserved exactly, because that flow always follows a submit.
 */

/** Read the saved cart. Accepts blobs written before this refactor. */
export function readSavedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const saved = JSON.parse(raw);
    if (Date.now() - saved.timestamp >= SAVED_STATE_MAX_AGE_MS) {
      // Expired blobs are deleted on read, not just ignored -- otherwise stale PII sits there
      // indefinitely for anyone who never returns.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const customer = {};
    CUSTOMER_FIELD_NAMES.forEach((name) => {
      if (saved[name]) customer[name] = saved[name];
    });

    return {
      selection: {
        primaryPlanId: saved.selectedPrimaryPlan || undefined,
        term: saved.selectedTerm || 'monthly',
        addonIds: saved.selectedAddonSubscriptions || {},
        oneTimeIds: saved.selectedOneTimeProducts || {},
        questionAnswers: saved.questionAnswers || {},
      },
      customer,
      coupon: { code: saved.couponCode || '', applied: saved.appliedCoupon || null },
      consent:
        typeof saved.mailchimpOptIn === 'boolean'
          ? { mailchimpOptIn: saved.mailchimpOptIn }
          : undefined,
    };
  } catch (error) {
    console.error('Error loading form state:', error);
    return null;
  }
}

/**
 * Persist the CART only -- no customer fields. Safe to call on every change.
 *
 * Any customer PII already stored (from a previous submit) is preserved, so a cart edit after
 * a cancelled checkout does not wipe the details the resume banner is about to restore.
 */
export function writeSavedState({ selection, coupon, consent }) {
  try {
    let existingPii = {};
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const prev = JSON.parse(raw);
        CUSTOMER_FIELD_NAMES.forEach((name) => {
          if (prev[name]) existingPii[name] = prev[name];
        });
      }
    } catch {
      // A corrupt blob is simply replaced.
    }

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        selectedPrimaryPlan: selection.primaryPlanId,
        selectedTerm: selection.term,
        selectedAddonSubscriptions: selection.addonIds,
        selectedOneTimeProducts: selection.oneTimeIds,
        questionAnswers: selection.questionAnswers,
        ...existingPii,
        couponCode: coupon.code,
        appliedCoupon: coupon.applied,
        mailchimpOptIn: consent.mailchimpOptIn,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.error('Error saving form state:', error);
  }
}

/**
 * Write the cart PLUS customer PII, called once, immediately before redirecting to Stripe.
 *
 * This is the only moment PII is persisted, and the only reason it is persisted at all: the
 * customer is leaving the page and may cancel and come back.
 */
export function persistForCheckout({ selection, customer, coupon, consent }) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        selectedPrimaryPlan: selection.primaryPlanId,
        selectedTerm: selection.term,
        selectedAddonSubscriptions: selection.addonIds,
        selectedOneTimeProducts: selection.oneTimeIds,
        questionAnswers: selection.questionAnswers,
        ...customer,
        couponCode: coupon.code,
        appliedCoupon: coupon.applied,
        mailchimpOptIn: consent.mailchimpOptIn,
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    console.error('Error saving form state:', error);
  }
}

/** Delete the saved cart and any PII in it. Called after a successful return from Stripe. */
export function clearSavedState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing form state:', error);
  }
}
